import { EventType } from "@/analytics/analytics-events";
import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { useDelayedHover } from "@/hooks/use-delayed-hover";
import {
  eventToScreenVec,
  screenVecToWorldVec,
} from "@/registration-tools/common/interaction/single-pin-interaction";
import { useThresholdSetContext } from "@/registration-tools/common/registration-report/threshold-set-context";
import {
  QualityStatus,
  RegistrationDetails,
} from "@/registration-tools/utils/metrics";
import {
  MultiRegistrationReport,
  reportScans,
} from "@/registration-tools/utils/multi-registration-report";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import {
  useLineRaycast,
  useNonExhaustiveEffect,
  useThreeEventTargetRef,
} from "@faro-lotv/app-component-toolbox";
import {
  ColorString,
  FaroPopover,
  FaroText,
  TranslateVar,
  green,
  grey,
  red,
  yellow,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { LineMaterial } from "@faro-lotv/lotv";
import {
  RegistrationEdgeRevision,
  RegistrationEdgeType,
  RegistrationRevision,
  RegistrationState,
  RevisionStatus,
  isRevisionScanEntity,
} from "@faro-lotv/service-wires";
import { SxProps, Theme } from "@mui/material";
import { useCursor } from "@react-three/drei";
import { ThreeEvent, useThree } from "@react-three/fiber";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Mesh, Vector2, Vector3, Vector3Tuple } from "three";
import { LineSegmentsGeometry } from "three-stdlib";
import { useEditConnectionContext } from "../edit-connection-context";
import {
  selectEntityIdToColorMap,
  selectHoveredEntityId,
  selectIsConnectionHighlighted,
  selectIsConnectionHovered,
} from "../store/data-preparation-ui/data-preparation-ui-selectors";
import {
  setHoveredConnectionId,
  setSelectedConnectionId,
  unsetHoveredConnectionId,
  unsetSelectedConnectionId,
} from "../store/data-preparation-ui/data-preparation-ui-slice";
import { selectQualityColorCoding } from "../store/data-preparation-view-options/data-preparation-view-options-selectors";
import { selectRevisionEntity } from "../store/revision/revision-entity-selectors";
import { selectRevisionEntityScan } from "../store/revision/revision-scan-selectors";
import {
  selectActivePartOfEdges,
  selectActiveRevisionEdges,
} from "../store/select-active-revision-edges";
import {
  selectIsDeletingConnections,
  selectIsTransformingScans,
} from "../store/user-edits/data-preparation-user-edit-selectors";
import { addUserEditDeleteConnection } from "../store/user-edits/data-preparation-user-edit-slice";
import { ConnectionQualityDialog } from "../ui/data-preparation-dialogs/data-preparation-connection-quality-dialog";
import {
  QualityPopover,
  QualityScope,
  VirtualElement,
} from "../ui/quality-popover";
import {
  HOVER_FACTOR,
  RenderOrder,
  useScanPosition,
} from "../utils/entity-utils";

/** The width of the connection lines. */
const LINE_WIDTH = 5;

/** How PartOf edges should differ in size to regular registration edges. */
const PART_OF_SIZE_FACTOR = 0.5;

/** The color of connection line when user chooses quality color to be disabled */
const DISABLED_QUALITY_COLOR = grey[700];

/** The color of connection line whose quality is not known */
const UNKNOWN_QUALITY_COLOR = grey[700];

type RegistrationConnectionsProps = {
  /** The registration state of the scans, used to display connections between scans. */
  revisionState?: RegistrationState;
};

/** @returns Visual representation of the registration connections. */
export function RegistrationConnections({
  revisionState,
}: RegistrationConnectionsProps): JSX.Element | null {
  const activeEdges = useAppSelector(selectActiveRevisionEdges);
  const activePartOfEdges = useAppSelector(selectActivePartOfEdges);
  const portal = useThreeEventTargetRef();

  return (
    <group name="registration connections">
      {activeEdges.map((edge) => (
        <ConnectionLine
          registrationEdge={edge}
          key={edge.id}
          portal={portal}
          revisionState={revisionState}
        />
      ))}

      {activePartOfEdges.map((edge) => (
        <ConnectionLine
          registrationEdge={edge}
          key={edge.id}
          portal={portal}
          revisionState={revisionState}
        />
      ))}

      <DrawUserAddedConnection />
    </group>
  );
}

/**
 * The limit of the z-index range for the scan markers.
 *
 * The limit needs to be sufficiently big so that the markers can be properly mapped according to their 3D position.
 */
const MARKER_Z_INDEX_LIMIT = 10_000_000;

/** A z-index value to place overlays on top of the scan markers. */
export const OVERLAY_Z_INDEX = MARKER_Z_INDEX_LIMIT + 1;

/** A z-index value to place tooltips/popovers on top of the overlays. */
export const TOOLTIP_Z_INDEX = OVERLAY_Z_INDEX + 1;

type ConnectionLineProps = {
  /** The registration edge to create a line for. */
  registrationEdge: RegistrationEdgeRevision;

  /** The portal element to place the HTML entities inside of. */
  portal?: MutableRefObject<HTMLElement>;

  /** Whether the line should be non-interactable. */
  nonInteractable?: boolean;

  /** The revision state of the scans, used to display connections between scans. */
  revisionState?: RegistrationState;
};

/** @returns A line representing a cloud-to-cloud registration. */
function ConnectionLine({
  registrationEdge,
  portal,
  revisionState,
}: ConnectionLineProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const { thresholdSet } = useThresholdSetContext();
  const currentUser = useAppSelector(selectCurrentUser);

  const { id, sourceId, targetId, data } = registrationEdge;

  const sourceEntity = useAppSelector(selectRevisionEntityScan(sourceId));
  const targetEntity = useAppSelector(selectRevisionEntityScan(targetId));
  const isConnectionHighlighted = useAppSelector(
    selectIsConnectionHighlighted(id),
  );
  const isConnectionHovering = useAppSelector(selectIsConnectionHovered(id));

  const isTransformingScans = useAppSelector(selectIsTransformingScans);

  const entityColorMap = useAppSelector(selectEntityIdToColorMap);

  const isPartOfEdge = registrationEdge.type === RegistrationEdgeType.partOf;

  const sourcePosition = useScanPosition(sourceEntity);
  const targetPosition = useScanPosition(targetEntity);

  const connectionPosition = useMemo(
    () =>
      new Vector3(
        (sourcePosition[0] + targetPosition[0]) / 2,
        (sourcePosition[1] + targetPosition[1]) / 2,
        (sourcePosition[2] + targetPosition[2]) / 2,
      ),
    [sourcePosition, targetPosition],
  );

  const [mousePosition, setMousePosition] = useState<
    { x: number; y: number } | undefined
  >(undefined);
  const [clickPosition, setClickPosition] = useState<
    { x: number; y: number } | undefined
  >(undefined);
  const [isConnectionQualityPopoverOpen, setIsConnectionQualityPopoverOpen] =
    useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  const showQualityColorCoding = useAppSelector(selectQualityColorCoding);
  const hasRegistrationDevFeature = useAppSelector(
    selectHasFeature(Features.RegistrationDev),
  );
  useCursor(isConnectionHovering);

  const registrationDetails = useMemo(
    () =>
      data?.metrics
        ? new RegistrationDetails(
            data.metrics.overlap,
            thresholdSet,
            data.metrics.rlyHistogram,
          )
        : undefined,
    [data?.metrics, thresholdSet],
  );

  const qualityPopoverAnchorEl = useMemo<VirtualElement>(
    () => ({
      getBoundingClientRect: () => {
        if (clickPosition === undefined) {
          throw new Error("Missing call to setClickPosition");
        }
        return {
          top: clickPosition.y,
          left: clickPosition.x,
          right: clickPosition.x,
          bottom: clickPosition.y,
          width: 0,
          height: 0,
          x: clickPosition.x,
          y: clickPosition.y,
          toJSON: () => {},
        };
      },
    }),
    [clickPosition],
  );

  const hoverPopoverAnchorEl = useMemo<VirtualElement>(
    () => ({
      getBoundingClientRect: () => {
        if (mousePosition === undefined) {
          throw new Error("Missing call to setMousePosition");
        }
        return {
          top: mousePosition.y,
          left: mousePosition.x,
          right: mousePosition.x,
          bottom: mousePosition.y,
          width: 0,
          height: 0,
          x: mousePosition.x,
          y: mousePosition.y,
          toJSON: () => {},
        };
      },
    }),
    [mousePosition],
  );

  const connectionOverallQuality = registrationDetails
    ? registrationDetails.registrationQuality
    : QualityStatus.UNKNOWN;

  // Determine the line color based on the quality of the registration
  const qualityColor = useMemo(() => {
    switch (connectionOverallQuality) {
      case QualityStatus.GOOD:
        return green[500];
      case QualityStatus.MEDIUM:
        return yellow[500];
      case QualityStatus.POOR:
        return red[500];
      default:
        return UNKNOWN_QUALITY_COLOR;
    }
  }, [connectionOverallQuality]);

  const connectionColor = useMemo(() => {
    if (!isConnectionHighlighted) {
      return grey[500];
    }
    if (isPartOfEdge) {
      // PartOf edges are shown in the color of the defining scan
      return entityColorMap[targetId] ?? DISABLED_QUALITY_COLOR;
    }
    return showQualityColorCoding ? qualityColor : DISABLED_QUALITY_COLOR;
  }, [
    isConnectionHighlighted,
    qualityColor,
    showQualityColorCoding,
    entityColorMap,
    targetId,
    isPartOfEdge,
  ]);

  const baseWidth = isPartOfEdge
    ? LINE_WIDTH * PART_OF_SIZE_FACTOR
    : LINE_WIDTH;
  const width =
    isConnectionHovering || isConnectionQualityPopoverOpen
      ? baseWidth * HOVER_FACTOR
      : baseWidth;
  const renderOrder = isPartOfEdge
    ? RenderOrder.partOfConnections
    : RenderOrder.connections;

  const onDelayedHoverStart = useCallback(
    (event: ThreeEvent<PointerEvent> | undefined) => {
      if (!showQualityColorCoding || isTransformingScans) return;

      if (event) {
        setMousePosition({ x: event.clientX, y: event.clientY });
      }
      dispatch(setHoveredConnectionId(id));
    },
    [dispatch, id, isTransformingScans, showQualityColorCoding],
  );
  const onDelayedHoverEnd = useCallback(() => {
    if (!showQualityColorCoding) return;

    dispatch(unsetHoveredConnectionId(id));
    setMousePosition(undefined);
  }, [dispatch, id, showQualityColorCoding]);

  const { onHoverStart, onHoverEnd } = useDelayedHover<
    ThreeEvent<PointerEvent> | undefined
  >({
    onDelayedHoverStart,
    onDelayedHoverEnd,
  });

  const onHoverChange = useCallback(
    (isHovering: boolean, event?: ThreeEvent<PointerEvent>) => {
      if (isHovering) {
        onHoverStart(event);
      } else {
        onHoverEnd(event);
      }
    },
    [onHoverEnd, onHoverStart],
  );

  if (!sourceEntity || !targetEntity) {
    return null;
  }

  return (
    <>
      <ConnectionLineSegment
        renderOrder={renderOrder}
        sourcePosition={sourcePosition}
        targetPosition={targetPosition}
        color={connectionColor}
        width={width}
        onClick={(event) => {
          if (selectIsDeletingConnections(getState()) && currentUser) {
            // Delete the connection
            Analytics.track(EventType.deleteRegistrationConnection);
            dispatch(
              addUserEditDeleteConnection({
                sourceId,
                targetId,
                createdBy: currentUser.id,
              }),
            );
            // Ensure that the deleted connection is not selected or hovered anymore
            dispatch(unsetSelectedConnectionId(id));
            dispatch(unsetHoveredConnectionId(id));
            return;
          }

          // Show the quality report of the registration
          if (showQualityColorCoding) {
            setClickPosition({ x: event.clientX, y: event.clientY });
            setIsConnectionQualityPopoverOpen(true);
            dispatch(setSelectedConnectionId(id));
            Analytics.track(EventType.showSimpleConnectionQualityReport);
            if (hasRegistrationDevFeature) {
              console.debug("Clicked edge debug data:", registrationEdge);
            }
          }
        }}
        onHoverChange={onHoverChange}
      />
      {/* The quality preview is in a separate HTML wrapper to ensure that it's always positioned above the scan markers and connections */}
      <AppAwareHtml
        position={connectionPosition}
        portal={portal}
        zIndexRange={[TOOLTIP_Z_INDEX, TOOLTIP_Z_INDEX]}
      >
        {isConnectionQualityPopoverOpen && (
          <QualityPopover
            aria-label="simple connection quality report"
            scope={QualityScope.CONNECTION}
            quality={connectionOverallQuality}
            registrationDetails={registrationDetails}
            anchorEl={qualityPopoverAnchorEl}
            placement="bottom"
            isOpen={isConnectionQualityPopoverOpen}
            onDetailsButtonClick={() => {
              setIsConnectionQualityPopoverOpen(false);
              dispatch(unsetSelectedConnectionId(id));
              setIsDetailsDialogOpen(true);
              Analytics.track(EventType.showDetailedConnectionQualityReport);
            }}
            closePopover={() => {
              setIsConnectionQualityPopoverOpen(false);
              dispatch(unsetSelectedConnectionId(id));
            }}
            connectionScans={{
              sourceScanName: sourceEntity.name,
              targetScanName: targetEntity.name,
            }}
            revisionStatus={revisionState}
            registrationEdgeType={registrationEdge.type}
          />
        )}

        {isConnectionHovering &&
          !isConnectionQualityPopoverOpen &&
          mousePosition && (
            <FaroPopover
              title="registration connection"
              anchorEl={hoverPopoverAnchorEl}
              placement="bottom"
              disablePortal
              open
              showCloseButton={false}
              dark
              sx={{ minWidth: "320px", pointerEvents: "none" }}
            >
              <ConnectionScanNames
                sourceScanName={sourceEntity.name}
                targetScanName={targetEntity.name}
              />
            </FaroPopover>
          )}

        <ConnectionQualityDialog
          registrationEdge={registrationEdge}
          isDialogOpen={isDetailsDialogOpen}
          onDialogClose={setIsDetailsDialogOpen}
        />
      </AppAwareHtml>
    </>
  );
}

type ConnectionLineSegmentProps = {
  renderOrder?: number;
  /** The start position of the line. */
  sourcePosition: Vector3Tuple;
  /** The end position of the line. */
  targetPosition: Vector3Tuple;
  /** The color of the line. */
  color: ColorString;
  /** The width of the line. */
  width: number;
  /** The action to execute when the user clicks on the line. */
  onClick?(event: ThreeEvent<MouseEvent>): void;
  /** The action to execute when the user hovers on the line. */
  onHoverChange?(isHovering: boolean, event?: ThreeEvent<PointerEvent>): void;
};

/** @returns A colored line segment with raycast support. */
function ConnectionLineSegment({
  renderOrder = RenderOrder.connections,
  sourcePosition,
  targetPosition,
  color,
  width,
  onClick,
  onHoverChange,
}: ConnectionLineSegmentProps): JSX.Element {
  const geometry = useMemo(() => {
    const g = new LineSegmentsGeometry();
    g.setPositions([...sourcePosition, ...targetPosition]);
    return g;
  }, [sourcePosition, targetPosition]);

  const canvasSize = useThree((state) => state.size);
  const resolution = useMemo(
    () => new Vector2(canvasSize.width, canvasSize.height),
    [canvasSize.height, canvasSize.width],
  );

  const mesh = useRef<Mesh<LineSegmentsGeometry, LineMaterial>>(null);
  const material = useRef<LineMaterial>(null);

  const raycast = useLineRaycast(mesh);

  // Prevent hover state being stuck as true when the component is unmounted
  useNonExhaustiveEffect(
    () => () => {
      onHoverChange?.(false);
    },
    [],
  );

  return (
    <mesh
      renderOrder={renderOrder}
      ref={mesh}
      onClick={(event) => {
        if (onClick) {
          event.stopPropagation();
          onClick(event);
        }
      }}
      onPointerEnter={(event) => {
        if (onHoverChange) {
          event.stopPropagation();
          onHoverChange(true, event);
        }
      }}
      onPointerLeave={(event) => {
        if (onHoverChange) {
          event.stopPropagation();
          onHoverChange(false, event);
        }
      }}
      // eslint-disable-next-line react/no-unknown-property
      raycast={raycast}
    >
      <primitive object={geometry} attach="geometry" />
      <lineMaterial
        ref={material}
        color={color}
        transparent
        depthTest={false}
        depthWrite
        /* eslint-disable react/no-unknown-property */
        worldUnits={false}
        linewidth={width}
        resolution={resolution}
        alphaToCoverage={false}
        borderWidth={0.5}
        borderColor={grey[1300]}
        /* eslint-enable react/no-unknown-property */
      />
    </mesh>
  );
}

/** Names of the scans in a connection */
export type ConnectionScans = {
  /** Name of one scan in the connection*/
  sourceScanName: string;
  /** Name of another scan in the connection*/
  targetScanName: string;
  /** styling */
  sx?: SxProps<Theme>;
};

/**
 * @param sourceScanName name of one of the scans in a connection
 * @param targetScanName name of another scan in a connection
 * @returns scan names of a connection in a specific design format
 */
export function ConnectionScanNames({
  sourceScanName,
  targetScanName,
  sx,
}: ConnectionScans): JSX.Element {
  return (
    <FaroText variant="bodyS" dark sx={{ ...sx, wordBreak: "break-word" }}>
      Scan name:
      <br />
      <FaroText aria-label="source capture name" variant="labelM" dark>
        <TranslateVar name="source-capture">{sourceScanName}</TranslateVar>
      </FaroText>
      <br />
      Scan name:
      <br />
      <FaroText aria-label="target capture name" variant="labelM" dark>
        <TranslateVar name="target-capture">{targetScanName}</TranslateVar>
      </FaroText>
    </FaroText>
  );
}

/**
 * @param report The registration report to get the connections from.
 * @param revision The revision that the connections are for.
 * @returns All scan connections that have been registered.
 */
export function reportConnections(
  report: MultiRegistrationReport,
  revision: RegistrationRevision,
): Array<RegistrationEdgeRevision & { type: RegistrationEdgeType.global }> {
  return reportScans(report).flatMap((scan) =>
    scan.registrations.map((registration) => ({
      id: registration.registrationObjectId,
      createdBy: revision.createdBy,
      createdAt: revision.createdAt,
      lastPatchedBy: revision.createdBy,
      lastPatchedAt: revision.createdAt,
      status: RevisionStatus.added,
      sourceId: scan.uuid,
      targetId: registration.targetScanId,
      type: RegistrationEdgeType.global,
      data: {
        jsonRevision: 3,
        metrics: registration.metrics,
      },
    })),
  );
}

/**
 * @returns A temporary visualization of the connection line as the user is drawing it in.
 *  The line is drawn from the selected scan to the cursor position.
 *  If the user is not adding connections, nothing is drawn.
 */
export function DrawUserAddedConnection(): JSX.Element | null {
  // Track cursor position on the entire window, draw the line on the canvas
  const portal = document.body;
  const canvas = useThree((state) => state.gl.domElement);

  const { sourceId } = useEditConnectionContext();
  const selectedScan = useAppSelector(selectRevisionEntityScan(sourceId));
  const startPos = useScanPosition(selectedScan);

  const hoveredEntityId = useAppSelector(selectHoveredEntityId);
  const hoveredEntity = useAppSelector(selectRevisionEntity(hoveredEntityId));
  const hoveredScan = useMemo(
    () =>
      hoveredEntity && isRevisionScanEntity(hoveredEntity)
        ? hoveredEntity
        : undefined,
    [hoveredEntity],
  );
  const hoveredPos = useScanPosition(hoveredScan);

  const cursorWorldPos = useRef(new Vector3());
  const [targetPos, setTargetPos] = useState(cursorWorldPos.current.toArray());
  const camera = useThree((state) => state.camera);

  // Register the event listeners to track the cursor position
  useEffect(() => {
    function updateCursorPosition(event: MouseEvent): void {
      if (!sourceId) return;

      screenVecToWorldVec(
        eventToScreenVec(canvas, event, new Vector3()),
        cursorWorldPos.current,
        camera,
        canvas.clientWidth,
        canvas.clientHeight,
      );
      setTargetPos(cursorWorldPos.current.toArray());
    }
    portal.addEventListener("mousemove", updateCursorPosition);

    return () => {
      portal.removeEventListener("mousemove", updateCursorPosition);
    };
  }, [portal, canvas, camera, sourceId]);

  // If a scan is hovered, draw the line to it, otherwise draw to the cursor
  const endPos = hoveredScan ? hoveredPos : targetPos;

  if (!sourceId) {
    return null;
  }

  return (
    <ConnectionLineSegment
      color={UNKNOWN_QUALITY_COLOR}
      width={LINE_WIDTH}
      sourcePosition={startPos}
      targetPosition={endPos}
    />
  );
}
